import { TypeformLogicJumpCondition } from '@/api/model/typeform/typeform-logic-jump-condition'
import { TypeformLogicJumpFlattenedCondition } from '@/api/model/typeform/typeform-logic-jump-flattened-condition'
import { v4 as uuid } from 'uuid'
import { cloneDeep } from 'lodash'

export class TypeformLogicJumpAction {
    constructor(
        readonly id: string,
        public action: string,
        public details: { to: { type: string, value: string } },
        public condition: TypeformLogicJumpCondition,
    ) {}

    get flattenedConditions(): TypeformLogicJumpFlattenedCondition[] {
        return this.flattenCondition(this.condition)
    }

    set flattenedConditions(flattenedConditions: TypeformLogicJumpFlattenedCondition[]) {
        let condition = null

        const conditions = cloneDeep(flattenedConditions)
        while (conditions.length > 0) {
            const flattenedCondition = conditions.shift()

            if (condition === null) {
                // Add condition
                condition = flattenedCondition.toCondition()
            } else if (flattenedCondition.flattenedOp === condition.op) {
                // Add to existing composite condition
                const vars = condition.vars as TypeformLogicJumpCondition[]
                vars.push(flattenedCondition.toCondition())
            } else if (flattenedCondition.flattenedOp === 'and' && condition.op === 'or') {
                const lastCondition = condition.vars[condition.vars.length - 1] as TypeformLogicJumpCondition

                if (lastCondition.op === 'and') {
                    // Add to existing composite AND condition
                    const vars = lastCondition.vars as TypeformLogicJumpCondition[]
                    vars.push(flattenedCondition.toCondition())
                } else {
                    // Convert existing condition to composite OR condition
                    condition.vars[condition.vars.length - 1] = {
                        op: flattenedCondition.flattenedOp,
                        vars: [
                            lastCondition,
                            flattenedCondition.toCondition(),
                        ],
                    }
                }
            } else {
                // Convert existing condition to composite condition
                condition = {
                    op: flattenedCondition.flattenedOp,
                    vars: [
                        condition,
                        flattenedCondition.toCondition(),
                    ],
                }
            }
        }

        this.condition = condition
    }

    private flattenCondition(condition, flattenedOp = null): TypeformLogicJumpFlattenedCondition[] {
        let flattenedConditions = []

        if (condition.op === 'and' || condition.op === 'or') {
            condition.vars.forEach(nestedCondition => {
                // Overwrite flattened op in case nested condition itself is a nested op
                const nestedFlattenedConditions = this.flattenCondition(nestedCondition, condition.op)
                nestedFlattenedConditions[0].flattenedOp = condition.op

                flattenedConditions = [].concat(flattenedConditions, nestedFlattenedConditions)
            })
        } else {
            flattenedConditions.push(TypeformLogicJumpFlattenedCondition.fromCondition(condition, flattenedOp))
        }

        return flattenedConditions
    }

    get alwaysCondition(): TypeformLogicJumpFlattenedCondition|null {
        return this.flattenedConditions.find(condition => condition.op === 'always')
    }

    toRequestData(): any {
        return {
            action: this.action,
            details: this.details,
            condition: this.condition,
        }
    }

    static fromData(data: any): TypeformLogicJumpAction {
        if (data === null || typeof data === 'string' || data instanceof TypeformLogicJumpAction) {
            return data
        }

        return new TypeformLogicJumpAction(
            data.id || uuid(),
            data.action,
            data.details,
            TypeformLogicJumpCondition.fromData(data.condition),
        )
    }
}
